'use strict';

exports.__esModule = true;

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

var _diode = require('diode');

var _diode2 = _interopRequireDefault(_diode);

var _storesMeta = require('./stores/meta');

var _storesMeta2 = _interopRequireDefault(_storesMeta);

var _Transaction = require('./Transaction');

var _Transaction2 = _interopRequireDefault(_Transaction);

var _Tree = require('./Tree');

var _Tree2 = _interopRequireDefault(_Tree);

var _coroutine = require('./coroutine');

var _coroutine2 = _interopRequireDefault(_coroutine);

var _defaults = require('./defaults');

var _defaults2 = _interopRequireDefault(_defaults);

var _dispatch = require('./dispatch');

var _dispatch2 = _interopRequireDefault(_dispatch);

var _eventually = require('./eventually');

var _eventually2 = _interopRequireDefault(_eventually);

var _flatten = require('./flatten');

var _flatten2 = _interopRequireDefault(_flatten);

var _install = require('./install');

var _install2 = _interopRequireDefault(_install);

var _lifecycle = require('./lifecycle');

var _lifecycle2 = _interopRequireDefault(_lifecycle);

var _merge = require('./merge');

var _merge2 = _interopRequireDefault(_merge);

var _tag = require('./tag');

var _tag2 = _interopRequireDefault(_tag);

var Microcosm = function Microcosm() {
  /**
   * Microcosm uses Diode for event emission. Diode is an event emitter
   * with a single event.
   * https://github.com/vigetlabs/diode
   */
  _diode2['default'](this);

  /**
   * Represents all "merged" transactions. Whenever a transaction completes,
   * the result is folded into base state and the transaction object is
   * "released". This lets transactions execute in a predicable order while
   * not soaking up memory keeping them forever.
   */
  this.base = {};

  /**
   * Holds publically available state. The result of folding all incomplete
   * transactions over base state. This property can safely be referenced when
   * retrieving application state.
   */
  this.state = this.base;

  this.stores = [];
  this.plugins = [];
  this.history = new _Tree2['default']();

  this.addStore(_storesMeta2['default']);
};

Microcosm.prototype = {
  constructor: Microcosm,

  getInitialState: function getInitialState() {
    return _dispatch2['default'](this.stores, {}, _Transaction2['default'](_lifecycle2['default'].willStart, this.state));
  },

  /**
   * Dispatch all open transactions upon base state to determine
   * a new state. This is the state exposed to the outside world.
   */
  rollforward: function rollforward() {
    var _this = this;

    this.state = this.history.branch().reduce(function (state, transaction) {
      return _dispatch2['default'](_this.stores, state, transaction);
    }, this.base);

    this.emit(this.state);

    return this;
  },

  shouldHistoryKeep: function shouldHistoryKeep(transaction) {
    return false;
  },

  clean: function clean(transaction) {
    if (transaction.complete && !this.shouldHistoryKeep(transaction)) {
      this.base = _dispatch2['default'](this.stores, this.base, transaction);
      return true;
    }
    return false;
  },

  transactionWillOpen: function transactionWillOpen(transaction) {
    this.history.append(transaction);
  },

  transactionWillUpdate: function transactionWillUpdate(transaction, error, payload, complete) {
    transaction.active = !error;
    transaction.complete = complete;
    transaction.error = error;
    transaction.payload = payload;
  },

  transactionWillClose: function transactionWillClose() {
    this.history.prune(this.clean, this);
  },

  /**
   * Resolves an action. As that action signals changes, it will update
   * a unique transaction. If an error occurs, it will mark it for clean up
   * and the change will disappear from history.
   */
  push: function push(action, params, callback) {
    var _this2 = this;

    var transaction = _Transaction2['default'](_tag2['default'](action));
    var body = action.apply(null, _flatten2['default'](params));

    this.transactionWillOpen(transaction);

    return _coroutine2['default'](body, function (error, payload, complete) {
      _this2.transactionWillUpdate(transaction, error, payload, complete);

      if (complete) {
        _this2.transactionWillClose(transaction);
        _eventually2['default'](callback, _this2, error, payload);
      }

      _this2.rollforward();
    });
  },

  /**
   * Partially applies `push`.
   */
  prepare: function prepare(action) {
    var _this3 = this;

    var params = arguments.length <= 1 || arguments[1] === undefined ? [] : arguments[1];

    return function (more, callback) {
      if (more === undefined) more = [];
      return _this3.push(action, _flatten2['default'](params, more), callback);
    };
  },

  /**
   * Reset by pushing an action that will always return
   * a given state.
   */
  reset: function reset(state, callback) {
    return this.push(_lifecycle2['default'].willReset, _merge2['default'](this.getInitialState(), state), callback);
  },

  /**
   * Resets to a given state, passing it through deserialize first
   */
  replace: function replace(data, callback) {
    return this.reset(this.deserialize(data), callback);
  },

  /**
   * Pushes a plugin in to the registry for a given microcosm.
   * When `app.start()` is called, it will execute plugins in
   * the order in which they have been added using this function.
   */
  addPlugin: function addPlugin(config, options) {
    if (process.env.NODE_ENV !== 'production') {
      if (!config) {
        throw TypeError('Expected a plugin, instead got ' + typeof config);
      }

      if (typeof config == 'object' && typeof config.register !== 'function') {
        throw TypeError('Expected plugin to have a register function, instead got ' + typeof config.register);
      }
    }

    var plugin = _merge2['default']({ app: this, options: options }, _defaults2['default'](config));

    this.plugins.push(plugin);

    return this;
  },

  /**
   * Generates a store based on the provided `config` and assigns it to
   * manage the provided `key`. Whenever this store responds to an action,
   * it will be provided the current state for that particular key.
   */
  addStore: function addStore(keyPath, store) {
    if (arguments.length < 2) {
      // Important! Assignment this way is important
      // to support IE9, which has an odd way of referencing
      // arguments
      store = keyPath;
      keyPath = [];
    }

    if (process.env.NODE_ENV !== 'production') {
      if (!store || typeof store !== 'function' && typeof store !== 'object') {
        throw TypeError('Expected a store object or function. Instead got: ' + store);
      }
    }

    this.stores.push([_flatten2['default'](keyPath), _defaults2['default'](store)]);

    return this;
  },

  /**
   * Returns an object that is the result of transforming application state
   * according to the `serialize` method described by each store.
   */
  serialize: function serialize() {
    return _dispatch2['default'](this.stores, this.state, _Transaction2['default'](_lifecycle2['default'].willSerialize, this.state));
  },

  /**
   * For each key in the provided `data` parameter, transform it using
   * the `deserialize` method provided by the store managing that key.
   * Then fold the deserialized data over the current application state.
   */
  deserialize: function deserialize(data) {
    return data == undefined ? this.state : _dispatch2['default'](this.stores, data, _Transaction2['default'](_lifecycle2['default'].willDeserialize, data));
  },

  /**
   * Alias for `serialize`
   */
  toJSON: function toJSON() {
    return this.serialize();
  },

  /**
   * Starts an application:
   * 1. Run through all plugins, it will terminate if any fail
   * 2. Execute the provided callback, passing along any errors
   *    generated if installing plugins fails.
   */
  start: function start(callback) {
    this.push(_lifecycle2['default'].willStart);

    _install2['default'](this.plugins, callback);

    return this;
  }

};

exports['default'] = Microcosm;
module.exports = exports['default'];
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInNyYy9NaWNyb2Nvc20uanMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6Ijs7Ozs7O3FCQUF3QixPQUFPOzs7OzBCQUNQLGVBQWU7Ozs7MkJBQ2YsZUFBZTs7OztvQkFDZixRQUFROzs7O3lCQUNSLGFBQWE7Ozs7d0JBQ2IsWUFBWTs7Ozt3QkFDWixZQUFZOzs7OzBCQUNaLGNBQWM7Ozs7dUJBQ2QsV0FBVzs7Ozt1QkFDWCxXQUFXOzs7O3lCQUNYLGFBQWE7Ozs7cUJBQ2IsU0FBUzs7OzttQkFDVCxPQUFPOzs7O0FBRS9CLElBQUksU0FBUyxHQUFHLFNBQVosU0FBUyxHQUFjOzs7Ozs7QUFNekIscUJBQU0sSUFBSSxDQUFDLENBQUE7Ozs7Ozs7O0FBUVgsTUFBSSxDQUFDLElBQUksR0FBRyxFQUFFLENBQUE7Ozs7Ozs7QUFPZCxNQUFJLENBQUMsS0FBSyxHQUFHLElBQUksQ0FBQyxJQUFJLENBQUE7O0FBRXRCLE1BQUksQ0FBQyxNQUFNLEdBQUksRUFBRSxDQUFBO0FBQ2pCLE1BQUksQ0FBQyxPQUFPLEdBQUcsRUFBRSxDQUFBO0FBQ2pCLE1BQUksQ0FBQyxPQUFPLEdBQUcsdUJBQVUsQ0FBQTs7QUFFekIsTUFBSSxDQUFDLFFBQVEseUJBQVcsQ0FBQTtDQUN6QixDQUFBOztBQUVELFNBQVMsQ0FBQyxTQUFTLEdBQUc7QUFDcEIsYUFBVyxFQUFFLFNBQVM7O0FBRXRCLGlCQUFlLEVBQUEsMkJBQUc7QUFDaEIsV0FBTyxzQkFBUyxJQUFJLENBQUMsTUFBTSxFQUFFLEVBQUUsRUFBRSx5QkFBWSx1QkFBVSxTQUFTLEVBQUUsSUFBSSxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUE7R0FDL0U7Ozs7OztBQU1ELGFBQVcsRUFBQSx1QkFBRzs7O0FBQ1osUUFBSSxDQUFDLEtBQUssR0FBRyxJQUFJLENBQUMsT0FBTyxDQUFDLE1BQU0sRUFBRSxDQUFDLE1BQU0sQ0FBQyxVQUFDLEtBQUssRUFBRSxXQUFXLEVBQUs7QUFDaEUsYUFBTyxzQkFBUyxNQUFLLE1BQU0sRUFBRSxLQUFLLEVBQUUsV0FBVyxDQUFDLENBQUE7S0FDakQsRUFBRSxJQUFJLENBQUMsSUFBSSxDQUFDLENBQUE7O0FBRWIsUUFBSSxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUE7O0FBRXJCLFdBQU8sSUFBSSxDQUFBO0dBQ1o7O0FBRUQsbUJBQWlCLEVBQUEsMkJBQUMsV0FBVyxFQUFFO0FBQzdCLFdBQU8sS0FBSyxDQUFBO0dBQ2I7O0FBRUQsT0FBSyxFQUFBLGVBQUMsV0FBVyxFQUFFO0FBQ2pCLFFBQUksV0FBVyxDQUFDLFFBQVEsSUFBSSxDQUFDLElBQUksQ0FBQyxpQkFBaUIsQ0FBQyxXQUFXLENBQUMsRUFBRTtBQUNoRSxVQUFJLENBQUMsSUFBSSxHQUFHLHNCQUFTLElBQUksQ0FBQyxNQUFNLEVBQUUsSUFBSSxDQUFDLElBQUksRUFBRSxXQUFXLENBQUMsQ0FBQTtBQUN6RCxhQUFPLElBQUksQ0FBQTtLQUNaO0FBQ0QsV0FBTyxLQUFLLENBQUE7R0FDYjs7QUFFRCxxQkFBbUIsRUFBQSw2QkFBQyxXQUFXLEVBQUU7QUFDL0IsUUFBSSxDQUFDLE9BQU8sQ0FBQyxNQUFNLENBQUMsV0FBVyxDQUFDLENBQUE7R0FDakM7O0FBRUQsdUJBQXFCLEVBQUEsK0JBQUMsV0FBVyxFQUFFLEtBQUssRUFBRSxPQUFPLEVBQUUsUUFBUSxFQUFFO0FBQzNELGVBQVcsQ0FBQyxNQUFNLEdBQUssQ0FBQyxLQUFLLENBQUE7QUFDN0IsZUFBVyxDQUFDLFFBQVEsR0FBRyxRQUFRLENBQUE7QUFDL0IsZUFBVyxDQUFDLEtBQUssR0FBTSxLQUFLLENBQUE7QUFDNUIsZUFBVyxDQUFDLE9BQU8sR0FBSSxPQUFPLENBQUE7R0FDL0I7O0FBRUQsc0JBQW9CLEVBQUEsZ0NBQUc7QUFDckIsUUFBSSxDQUFDLE9BQU8sQ0FBQyxLQUFLLENBQUMsSUFBSSxDQUFDLEtBQUssRUFBRSxJQUFJLENBQUMsQ0FBQTtHQUNyQzs7Ozs7OztBQU9ELE1BQUksRUFBQSxjQUFDLE1BQU0sRUFBRSxNQUFNLEVBQUUsUUFBUSxFQUFFOzs7QUFDN0IsUUFBSSxXQUFXLEdBQUcseUJBQVksaUJBQUksTUFBTSxDQUFDLENBQUMsQ0FBQTtBQUMxQyxRQUFJLElBQUksR0FBRyxNQUFNLENBQUMsS0FBSyxDQUFDLElBQUksRUFBRSxxQkFBUSxNQUFNLENBQUMsQ0FBQyxDQUFBOztBQUU5QyxRQUFJLENBQUMsbUJBQW1CLENBQUMsV0FBVyxDQUFDLENBQUE7O0FBRXJDLFdBQU8sdUJBQVUsSUFBSSxFQUFFLFVBQUMsS0FBSyxFQUFFLE9BQU8sRUFBRSxRQUFRLEVBQUs7QUFDbkQsYUFBSyxxQkFBcUIsQ0FBQyxXQUFXLEVBQUUsS0FBSyxFQUFFLE9BQU8sRUFBRSxRQUFRLENBQUMsQ0FBQTs7QUFFakUsVUFBSSxRQUFRLEVBQUU7QUFDWixlQUFLLG9CQUFvQixDQUFDLFdBQVcsQ0FBQyxDQUFBO0FBQ3RDLGdDQUFXLFFBQVEsVUFBUSxLQUFLLEVBQUUsT0FBTyxDQUFDLENBQUE7T0FDM0M7O0FBRUQsYUFBSyxXQUFXLEVBQUUsQ0FBQTtLQUNuQixDQUFDLENBQUE7R0FDSDs7Ozs7QUFLRCxTQUFPLEVBQUEsaUJBQUMsTUFBTSxFQUFhOzs7UUFBWCxNQUFNLHlEQUFDLEVBQUU7O0FBQ3ZCLFdBQU8sVUFBQyxJQUFJLEVBQUssUUFBUTtVQUFqQixJQUFJLGdCQUFKLElBQUksR0FBQyxFQUFFO2FBQWUsT0FBSyxJQUFJLENBQUMsTUFBTSxFQUFFLHFCQUFRLE1BQU0sRUFBRSxJQUFJLENBQUMsRUFBRSxRQUFRLENBQUM7S0FBQSxDQUFBO0dBQ2pGOzs7Ozs7QUFNRCxPQUFLLEVBQUEsZUFBQyxLQUFLLEVBQUUsUUFBUSxFQUFFO0FBQ3JCLFdBQU8sSUFBSSxDQUFDLElBQUksQ0FBQyx1QkFBVSxTQUFTLEVBQUUsbUJBQU0sSUFBSSxDQUFDLGVBQWUsRUFBRSxFQUFFLEtBQUssQ0FBQyxFQUFFLFFBQVEsQ0FBQyxDQUFBO0dBQ3RGOzs7OztBQUtELFNBQU8sRUFBQSxpQkFBQyxJQUFJLEVBQUUsUUFBUSxFQUFFO0FBQ3RCLFdBQU8sSUFBSSxDQUFDLEtBQUssQ0FBQyxJQUFJLENBQUMsV0FBVyxDQUFDLElBQUksQ0FBQyxFQUFFLFFBQVEsQ0FBQyxDQUFBO0dBQ3BEOzs7Ozs7O0FBT0QsV0FBUyxFQUFBLG1CQUFDLE1BQU0sRUFBRSxPQUFPLEVBQUU7QUFDekIsUUFBSSxPQUFPLENBQUMsR0FBRyxDQUFDLFFBQVEsS0FBSyxZQUFZLEVBQUU7QUFDekMsVUFBSSxDQUFDLE1BQU0sRUFBRTtBQUNYLGNBQU0sU0FBUyxDQUFDLGlDQUFpQyxHQUFHLE9BQU8sTUFBTSxDQUFDLENBQUE7T0FDbkU7O0FBRUQsVUFBSSxPQUFPLE1BQU0sSUFBSSxRQUFRLElBQUksT0FBTyxNQUFNLENBQUMsUUFBUSxLQUFLLFVBQVUsRUFBRTtBQUN0RSxjQUFNLFNBQVMsQ0FBQywyREFBMkQsR0FBRyxPQUFPLE1BQU0sQ0FBQyxRQUFRLENBQUMsQ0FBQTtPQUN0RztLQUNGOztBQUVELFFBQUksTUFBTSxHQUFHLG1CQUFNLEVBQUUsR0FBRyxFQUFFLElBQUksRUFBRSxPQUFPLEVBQVAsT0FBTyxFQUFFLEVBQUUsc0JBQVMsTUFBTSxDQUFDLENBQUMsQ0FBQTs7QUFFNUQsUUFBSSxDQUFDLE9BQU8sQ0FBQyxJQUFJLENBQUMsTUFBTSxDQUFDLENBQUE7O0FBRXpCLFdBQU8sSUFBSSxDQUFBO0dBQ1o7Ozs7Ozs7QUFPRCxVQUFRLEVBQUEsa0JBQUMsT0FBTyxFQUFFLEtBQUssRUFBRTtBQUN2QixRQUFJLFNBQVMsQ0FBQyxNQUFNLEdBQUcsQ0FBQyxFQUFFOzs7O0FBSXhCLFdBQUssR0FBSyxPQUFPLENBQUE7QUFDakIsYUFBTyxHQUFHLEVBQUUsQ0FBQztLQUNkOztBQUVELFFBQUksT0FBTyxDQUFDLEdBQUcsQ0FBQyxRQUFRLEtBQUssWUFBWSxFQUFFO0FBQ3pDLFVBQUksQ0FBQyxLQUFLLElBQUksT0FBTyxLQUFLLEtBQUssVUFBVSxJQUFJLE9BQU8sS0FBSyxLQUFLLFFBQVEsRUFBRTtBQUN0RSxjQUFNLFNBQVMsQ0FBQyxvREFBb0QsR0FBRyxLQUFLLENBQUMsQ0FBQTtPQUM5RTtLQUNGOztBQUVELFFBQUksQ0FBQyxNQUFNLENBQUMsSUFBSSxDQUFDLENBQUUscUJBQVEsT0FBTyxDQUFDLEVBQUUsc0JBQVMsS0FBSyxDQUFDLENBQUUsQ0FBQyxDQUFBOztBQUV2RCxXQUFPLElBQUksQ0FBQTtHQUNaOzs7Ozs7QUFNRCxXQUFTLEVBQUEscUJBQUc7QUFDVixXQUFPLHNCQUFTLElBQUksQ0FBQyxNQUFNLEVBQUUsSUFBSSxDQUFDLEtBQUssRUFBRSx5QkFBWSx1QkFBVSxhQUFhLEVBQUUsSUFBSSxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUE7R0FDM0Y7Ozs7Ozs7QUFPRCxhQUFXLEVBQUEscUJBQUMsSUFBSSxFQUFFO0FBQ2hCLFdBQU8sSUFBSSxJQUFJLFNBQVMsR0FBRyxJQUFJLENBQUMsS0FBSyxHQUNWLHNCQUFTLElBQUksQ0FBQyxNQUFNLEVBQUUsSUFBSSxFQUFFLHlCQUFZLHVCQUFVLGVBQWUsRUFBRSxJQUFJLENBQUMsQ0FBQyxDQUFBO0dBQ3JHOzs7OztBQUtELFFBQU0sRUFBQSxrQkFBRztBQUNQLFdBQU8sSUFBSSxDQUFDLFNBQVMsRUFBRSxDQUFBO0dBQ3hCOzs7Ozs7OztBQVFELE9BQUssRUFBQSxlQUFDLFFBQVEsRUFBRTtBQUNkLFFBQUksQ0FBQyxJQUFJLENBQUMsdUJBQVUsU0FBUyxDQUFDLENBQUE7O0FBRTlCLHlCQUFRLElBQUksQ0FBQyxPQUFPLEVBQUUsUUFBUSxDQUFDLENBQUE7O0FBRS9CLFdBQU8sSUFBSSxDQUFBO0dBQ1o7O0NBRUYsQ0FBQTs7cUJBRWMsU0FBUyIsImZpbGUiOiJzcmMvTWljcm9jb3NtLmpzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IERpb2RlICAgICAgIGZyb20gJ2Rpb2RlJ1xuaW1wb3J0IE1ldGFTdG9yZSAgIGZyb20gJy4vc3RvcmVzL21ldGEnXG5pbXBvcnQgVHJhbnNhY3Rpb24gZnJvbSAnLi9UcmFuc2FjdGlvbidcbmltcG9ydCBUcmVlICAgICAgICBmcm9tICcuL1RyZWUnXG5pbXBvcnQgY29yb3V0aW5lICAgZnJvbSAnLi9jb3JvdXRpbmUnXG5pbXBvcnQgZGVmYXVsdHMgICAgZnJvbSAnLi9kZWZhdWx0cydcbmltcG9ydCBkaXNwYXRjaCAgICBmcm9tICcuL2Rpc3BhdGNoJ1xuaW1wb3J0IGV2ZW50dWFsbHkgIGZyb20gJy4vZXZlbnR1YWxseSdcbmltcG9ydCBmbGF0dGVuICAgICBmcm9tICcuL2ZsYXR0ZW4nXG5pbXBvcnQgaW5zdGFsbCAgICAgZnJvbSAnLi9pbnN0YWxsJ1xuaW1wb3J0IGxpZmVjeWNsZSAgIGZyb20gJy4vbGlmZWN5Y2xlJ1xuaW1wb3J0IG1lcmdlICAgICAgIGZyb20gJy4vbWVyZ2UnXG5pbXBvcnQgdGFnICAgICAgICAgZnJvbSAnLi90YWcnXG5cbmxldCBNaWNyb2Nvc20gPSBmdW5jdGlvbigpIHtcbiAgLyoqXG4gICAqIE1pY3JvY29zbSB1c2VzIERpb2RlIGZvciBldmVudCBlbWlzc2lvbi4gRGlvZGUgaXMgYW4gZXZlbnQgZW1pdHRlclxuICAgKiB3aXRoIGEgc2luZ2xlIGV2ZW50LlxuICAgKiBodHRwczovL2dpdGh1Yi5jb20vdmlnZXRsYWJzL2Rpb2RlXG4gICAqL1xuICBEaW9kZSh0aGlzKVxuXG4gIC8qKlxuICAgKiBSZXByZXNlbnRzIGFsbCBcIm1lcmdlZFwiIHRyYW5zYWN0aW9ucy4gV2hlbmV2ZXIgYSB0cmFuc2FjdGlvbiBjb21wbGV0ZXMsXG4gICAqIHRoZSByZXN1bHQgaXMgZm9sZGVkIGludG8gYmFzZSBzdGF0ZSBhbmQgdGhlIHRyYW5zYWN0aW9uIG9iamVjdCBpc1xuICAgKiBcInJlbGVhc2VkXCIuIFRoaXMgbGV0cyB0cmFuc2FjdGlvbnMgZXhlY3V0ZSBpbiBhIHByZWRpY2FibGUgb3JkZXIgd2hpbGVcbiAgICogbm90IHNvYWtpbmcgdXAgbWVtb3J5IGtlZXBpbmcgdGhlbSBmb3JldmVyLlxuICAgKi9cbiAgdGhpcy5iYXNlID0ge31cblxuICAvKipcbiAgICogSG9sZHMgcHVibGljYWxseSBhdmFpbGFibGUgc3RhdGUuIFRoZSByZXN1bHQgb2YgZm9sZGluZyBhbGwgaW5jb21wbGV0ZVxuICAgKiB0cmFuc2FjdGlvbnMgb3ZlciBiYXNlIHN0YXRlLiBUaGlzIHByb3BlcnR5IGNhbiBzYWZlbHkgYmUgcmVmZXJlbmNlZCB3aGVuXG4gICAqIHJldHJpZXZpbmcgYXBwbGljYXRpb24gc3RhdGUuXG4gICAqL1xuICB0aGlzLnN0YXRlID0gdGhpcy5iYXNlXG5cbiAgdGhpcy5zdG9yZXMgID0gW11cbiAgdGhpcy5wbHVnaW5zID0gW11cbiAgdGhpcy5oaXN0b3J5ID0gbmV3IFRyZWUoKVxuXG4gIHRoaXMuYWRkU3RvcmUoTWV0YVN0b3JlKVxufVxuXG5NaWNyb2Nvc20ucHJvdG90eXBlID0ge1xuICBjb25zdHJ1Y3RvcjogTWljcm9jb3NtLFxuXG4gIGdldEluaXRpYWxTdGF0ZSgpIHtcbiAgICByZXR1cm4gZGlzcGF0Y2godGhpcy5zdG9yZXMsIHt9LCBUcmFuc2FjdGlvbihsaWZlY3ljbGUud2lsbFN0YXJ0LCB0aGlzLnN0YXRlKSlcbiAgfSxcblxuICAvKipcbiAgICogRGlzcGF0Y2ggYWxsIG9wZW4gdHJhbnNhY3Rpb25zIHVwb24gYmFzZSBzdGF0ZSB0byBkZXRlcm1pbmVcbiAgICogYSBuZXcgc3RhdGUuIFRoaXMgaXMgdGhlIHN0YXRlIGV4cG9zZWQgdG8gdGhlIG91dHNpZGUgd29ybGQuXG4gICAqL1xuICByb2xsZm9yd2FyZCgpIHtcbiAgICB0aGlzLnN0YXRlID0gdGhpcy5oaXN0b3J5LmJyYW5jaCgpLnJlZHVjZSgoc3RhdGUsIHRyYW5zYWN0aW9uKSA9PiB7XG4gICAgICByZXR1cm4gZGlzcGF0Y2godGhpcy5zdG9yZXMsIHN0YXRlLCB0cmFuc2FjdGlvbilcbiAgICB9LCB0aGlzLmJhc2UpXG5cbiAgICB0aGlzLmVtaXQodGhpcy5zdGF0ZSlcblxuICAgIHJldHVybiB0aGlzXG4gIH0sXG5cbiAgc2hvdWxkSGlzdG9yeUtlZXAodHJhbnNhY3Rpb24pIHtcbiAgICByZXR1cm4gZmFsc2VcbiAgfSxcblxuICBjbGVhbih0cmFuc2FjdGlvbikge1xuICAgIGlmICh0cmFuc2FjdGlvbi5jb21wbGV0ZSAmJiAhdGhpcy5zaG91bGRIaXN0b3J5S2VlcCh0cmFuc2FjdGlvbikpIHtcbiAgICAgIHRoaXMuYmFzZSA9IGRpc3BhdGNoKHRoaXMuc3RvcmVzLCB0aGlzLmJhc2UsIHRyYW5zYWN0aW9uKVxuICAgICAgcmV0dXJuIHRydWVcbiAgICB9XG4gICAgcmV0dXJuIGZhbHNlXG4gIH0sXG5cbiAgdHJhbnNhY3Rpb25XaWxsT3Blbih0cmFuc2FjdGlvbikge1xuICAgIHRoaXMuaGlzdG9yeS5hcHBlbmQodHJhbnNhY3Rpb24pXG4gIH0sXG5cbiAgdHJhbnNhY3Rpb25XaWxsVXBkYXRlKHRyYW5zYWN0aW9uLCBlcnJvciwgcGF5bG9hZCwgY29tcGxldGUpIHtcbiAgICB0cmFuc2FjdGlvbi5hY3RpdmUgICA9ICFlcnJvclxuICAgIHRyYW5zYWN0aW9uLmNvbXBsZXRlID0gY29tcGxldGVcbiAgICB0cmFuc2FjdGlvbi5lcnJvciAgICA9IGVycm9yXG4gICAgdHJhbnNhY3Rpb24ucGF5bG9hZCAgPSBwYXlsb2FkXG4gIH0sXG5cbiAgdHJhbnNhY3Rpb25XaWxsQ2xvc2UoKSB7XG4gICAgdGhpcy5oaXN0b3J5LnBydW5lKHRoaXMuY2xlYW4sIHRoaXMpXG4gIH0sXG5cbiAgLyoqXG4gICAqIFJlc29sdmVzIGFuIGFjdGlvbi4gQXMgdGhhdCBhY3Rpb24gc2lnbmFscyBjaGFuZ2VzLCBpdCB3aWxsIHVwZGF0ZVxuICAgKiBhIHVuaXF1ZSB0cmFuc2FjdGlvbi4gSWYgYW4gZXJyb3Igb2NjdXJzLCBpdCB3aWxsIG1hcmsgaXQgZm9yIGNsZWFuIHVwXG4gICAqIGFuZCB0aGUgY2hhbmdlIHdpbGwgZGlzYXBwZWFyIGZyb20gaGlzdG9yeS5cbiAgICovXG4gIHB1c2goYWN0aW9uLCBwYXJhbXMsIGNhbGxiYWNrKSB7XG4gICAgbGV0IHRyYW5zYWN0aW9uID0gVHJhbnNhY3Rpb24odGFnKGFjdGlvbikpXG4gICAgbGV0IGJvZHkgPSBhY3Rpb24uYXBwbHkobnVsbCwgZmxhdHRlbihwYXJhbXMpKVxuXG4gICAgdGhpcy50cmFuc2FjdGlvbldpbGxPcGVuKHRyYW5zYWN0aW9uKVxuXG4gICAgcmV0dXJuIGNvcm91dGluZShib2R5LCAoZXJyb3IsIHBheWxvYWQsIGNvbXBsZXRlKSA9PiB7XG4gICAgICB0aGlzLnRyYW5zYWN0aW9uV2lsbFVwZGF0ZSh0cmFuc2FjdGlvbiwgZXJyb3IsIHBheWxvYWQsIGNvbXBsZXRlKVxuXG4gICAgICBpZiAoY29tcGxldGUpIHtcbiAgICAgICAgdGhpcy50cmFuc2FjdGlvbldpbGxDbG9zZSh0cmFuc2FjdGlvbilcbiAgICAgICAgZXZlbnR1YWxseShjYWxsYmFjaywgdGhpcywgZXJyb3IsIHBheWxvYWQpXG4gICAgICB9XG5cbiAgICAgIHRoaXMucm9sbGZvcndhcmQoKVxuICAgIH0pXG4gIH0sXG5cbiAgLyoqXG4gICAqIFBhcnRpYWxseSBhcHBsaWVzIGBwdXNoYC5cbiAgICovXG4gIHByZXBhcmUoYWN0aW9uLCBwYXJhbXM9W10pIHtcbiAgICByZXR1cm4gKG1vcmU9W10sIGNhbGxiYWNrKSA9PiB0aGlzLnB1c2goYWN0aW9uLCBmbGF0dGVuKHBhcmFtcywgbW9yZSksIGNhbGxiYWNrKVxuICB9LFxuXG4gIC8qKlxuICAgKiBSZXNldCBieSBwdXNoaW5nIGFuIGFjdGlvbiB0aGF0IHdpbGwgYWx3YXlzIHJldHVyblxuICAgKiBhIGdpdmVuIHN0YXRlLlxuICAgKi9cbiAgcmVzZXQoc3RhdGUsIGNhbGxiYWNrKSB7XG4gICAgcmV0dXJuIHRoaXMucHVzaChsaWZlY3ljbGUud2lsbFJlc2V0LCBtZXJnZSh0aGlzLmdldEluaXRpYWxTdGF0ZSgpLCBzdGF0ZSksIGNhbGxiYWNrKVxuICB9LFxuXG4gIC8qKlxuICAgKiBSZXNldHMgdG8gYSBnaXZlbiBzdGF0ZSwgcGFzc2luZyBpdCB0aHJvdWdoIGRlc2VyaWFsaXplIGZpcnN0XG4gICAqL1xuICByZXBsYWNlKGRhdGEsIGNhbGxiYWNrKSB7XG4gICAgcmV0dXJuIHRoaXMucmVzZXQodGhpcy5kZXNlcmlhbGl6ZShkYXRhKSwgY2FsbGJhY2spXG4gIH0sXG5cbiAgLyoqXG4gICAqIFB1c2hlcyBhIHBsdWdpbiBpbiB0byB0aGUgcmVnaXN0cnkgZm9yIGEgZ2l2ZW4gbWljcm9jb3NtLlxuICAgKiBXaGVuIGBhcHAuc3RhcnQoKWAgaXMgY2FsbGVkLCBpdCB3aWxsIGV4ZWN1dGUgcGx1Z2lucyBpblxuICAgKiB0aGUgb3JkZXIgaW4gd2hpY2ggdGhleSBoYXZlIGJlZW4gYWRkZWQgdXNpbmcgdGhpcyBmdW5jdGlvbi5cbiAgICovXG4gIGFkZFBsdWdpbihjb25maWcsIG9wdGlvbnMpIHtcbiAgICBpZiAocHJvY2Vzcy5lbnYuTk9ERV9FTlYgIT09ICdwcm9kdWN0aW9uJykge1xuICAgICAgaWYgKCFjb25maWcpIHtcbiAgICAgICAgdGhyb3cgVHlwZUVycm9yKCdFeHBlY3RlZCBhIHBsdWdpbiwgaW5zdGVhZCBnb3QgJyArIHR5cGVvZiBjb25maWcpXG4gICAgICB9XG5cbiAgICAgIGlmICh0eXBlb2YgY29uZmlnID09ICdvYmplY3QnICYmIHR5cGVvZiBjb25maWcucmVnaXN0ZXIgIT09ICdmdW5jdGlvbicpIHtcbiAgICAgICAgdGhyb3cgVHlwZUVycm9yKCdFeHBlY3RlZCBwbHVnaW4gdG8gaGF2ZSBhIHJlZ2lzdGVyIGZ1bmN0aW9uLCBpbnN0ZWFkIGdvdCAnICsgdHlwZW9mIGNvbmZpZy5yZWdpc3RlcilcbiAgICAgIH1cbiAgICB9XG5cbiAgICBsZXQgcGx1Z2luID0gbWVyZ2UoeyBhcHA6IHRoaXMsIG9wdGlvbnMgfSwgZGVmYXVsdHMoY29uZmlnKSlcblxuICAgIHRoaXMucGx1Z2lucy5wdXNoKHBsdWdpbilcblxuICAgIHJldHVybiB0aGlzXG4gIH0sXG5cbiAgLyoqXG4gICAqIEdlbmVyYXRlcyBhIHN0b3JlIGJhc2VkIG9uIHRoZSBwcm92aWRlZCBgY29uZmlnYCBhbmQgYXNzaWducyBpdCB0b1xuICAgKiBtYW5hZ2UgdGhlIHByb3ZpZGVkIGBrZXlgLiBXaGVuZXZlciB0aGlzIHN0b3JlIHJlc3BvbmRzIHRvIGFuIGFjdGlvbixcbiAgICogaXQgd2lsbCBiZSBwcm92aWRlZCB0aGUgY3VycmVudCBzdGF0ZSBmb3IgdGhhdCBwYXJ0aWN1bGFyIGtleS5cbiAgICovXG4gIGFkZFN0b3JlKGtleVBhdGgsIHN0b3JlKSB7XG4gICAgaWYgKGFyZ3VtZW50cy5sZW5ndGggPCAyKSB7XG4gICAgICAvLyBJbXBvcnRhbnQhIEFzc2lnbm1lbnQgdGhpcyB3YXkgaXMgaW1wb3J0YW50XG4gICAgICAvLyB0byBzdXBwb3J0IElFOSwgd2hpY2ggaGFzIGFuIG9kZCB3YXkgb2YgcmVmZXJlbmNpbmdcbiAgICAgIC8vIGFyZ3VtZW50c1xuICAgICAgc3RvcmUgICA9IGtleVBhdGhcbiAgICAgIGtleVBhdGggPSBbXTtcbiAgICB9XG5cbiAgICBpZiAocHJvY2Vzcy5lbnYuTk9ERV9FTlYgIT09ICdwcm9kdWN0aW9uJykge1xuICAgICAgaWYgKCFzdG9yZSB8fCB0eXBlb2Ygc3RvcmUgIT09ICdmdW5jdGlvbicgJiYgdHlwZW9mIHN0b3JlICE9PSAnb2JqZWN0Jykge1xuICAgICAgICB0aHJvdyBUeXBlRXJyb3IoJ0V4cGVjdGVkIGEgc3RvcmUgb2JqZWN0IG9yIGZ1bmN0aW9uLiBJbnN0ZWFkIGdvdDogJyArIHN0b3JlKVxuICAgICAgfVxuICAgIH1cblxuICAgIHRoaXMuc3RvcmVzLnB1c2goWyBmbGF0dGVuKGtleVBhdGgpLCBkZWZhdWx0cyhzdG9yZSkgXSlcblxuICAgIHJldHVybiB0aGlzXG4gIH0sXG5cbiAgLyoqXG4gICAqIFJldHVybnMgYW4gb2JqZWN0IHRoYXQgaXMgdGhlIHJlc3VsdCBvZiB0cmFuc2Zvcm1pbmcgYXBwbGljYXRpb24gc3RhdGVcbiAgICogYWNjb3JkaW5nIHRvIHRoZSBgc2VyaWFsaXplYCBtZXRob2QgZGVzY3JpYmVkIGJ5IGVhY2ggc3RvcmUuXG4gICAqL1xuICBzZXJpYWxpemUoKSB7XG4gICAgcmV0dXJuIGRpc3BhdGNoKHRoaXMuc3RvcmVzLCB0aGlzLnN0YXRlLCBUcmFuc2FjdGlvbihsaWZlY3ljbGUud2lsbFNlcmlhbGl6ZSwgdGhpcy5zdGF0ZSkpXG4gIH0sXG5cbiAgLyoqXG4gICAqIEZvciBlYWNoIGtleSBpbiB0aGUgcHJvdmlkZWQgYGRhdGFgIHBhcmFtZXRlciwgdHJhbnNmb3JtIGl0IHVzaW5nXG4gICAqIHRoZSBgZGVzZXJpYWxpemVgIG1ldGhvZCBwcm92aWRlZCBieSB0aGUgc3RvcmUgbWFuYWdpbmcgdGhhdCBrZXkuXG4gICAqIFRoZW4gZm9sZCB0aGUgZGVzZXJpYWxpemVkIGRhdGEgb3ZlciB0aGUgY3VycmVudCBhcHBsaWNhdGlvbiBzdGF0ZS5cbiAgICovXG4gIGRlc2VyaWFsaXplKGRhdGEpIHtcbiAgICByZXR1cm4gZGF0YSA9PSB1bmRlZmluZWQgPyB0aGlzLnN0YXRlXG4gICAgICAgICAgICAgICAgICAgICAgICAgICAgIDogZGlzcGF0Y2godGhpcy5zdG9yZXMsIGRhdGEsIFRyYW5zYWN0aW9uKGxpZmVjeWNsZS53aWxsRGVzZXJpYWxpemUsIGRhdGEpKVxuICB9LFxuXG4gIC8qKlxuICAgKiBBbGlhcyBmb3IgYHNlcmlhbGl6ZWBcbiAgICovXG4gIHRvSlNPTigpIHtcbiAgICByZXR1cm4gdGhpcy5zZXJpYWxpemUoKVxuICB9LFxuXG4gIC8qKlxuICAgKiBTdGFydHMgYW4gYXBwbGljYXRpb246XG4gICAqIDEuIFJ1biB0aHJvdWdoIGFsbCBwbHVnaW5zLCBpdCB3aWxsIHRlcm1pbmF0ZSBpZiBhbnkgZmFpbFxuICAgKiAyLiBFeGVjdXRlIHRoZSBwcm92aWRlZCBjYWxsYmFjaywgcGFzc2luZyBhbG9uZyBhbnkgZXJyb3JzXG4gICAqICAgIGdlbmVyYXRlZCBpZiBpbnN0YWxsaW5nIHBsdWdpbnMgZmFpbHMuXG4gICAqL1xuICBzdGFydChjYWxsYmFjaykge1xuICAgIHRoaXMucHVzaChsaWZlY3ljbGUud2lsbFN0YXJ0KVxuXG4gICAgaW5zdGFsbCh0aGlzLnBsdWdpbnMsIGNhbGxiYWNrKVxuXG4gICAgcmV0dXJuIHRoaXNcbiAgfVxuXG59XG5cbmV4cG9ydCBkZWZhdWx0IE1pY3JvY29zbVxuIl19




/*****************
 ** WEBPACK FOOTER
 ** ./~/microcosm/Microcosm.js
 ** module id = 191
 ** module chunks = 0
 **/